import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactDom from 'react-dom';
import { connect, useDispatch, Provider } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps, MapDrawFuncParams } from 'types';
import Map from 'components/Map';
import MapSettings from 'components/Map/settings';
import {
  addGraphicFromGeoJson,
  getGeoJsonFromGraphics,
  addAoiToDrawingLayer,
  clearGraphics,
  enableClickOnMap,
  zoomToExtent,
  zoomToTenantExtent,
  setDrawingLayerTransparent,
  setPreviewLayerTransparent
} from '../../drawingFunction';
import {
  initialAOIPageStart,
  putNewAOIStart,
  revertUploadNewAOI,
  setPreviewId,
  updateGeoJsonFromMap,
  getCurrentAOIInfoStart,
  getPreviewAOIInfoStart
} from 'redux/areaOfInterest/actions';
import { addAlert, removeAlertByGroup } from 'redux/alerts/actions';
import {
  getFeatureServerUrl,
  featureRequestInterceptors
} from 'services/map.service';
import {
  queryFeatureLayerFeaturesStart,
  queryFeatureLayerExtentStart,
  clearFeatureLayerLayerExtent
} from 'redux/map/actions';
import { useComponentMount } from 'utilities/hooks.util';
import Tools from './tools';
import Controls from './controls';
import { Networking_06 as Networking06 } from '@carbon/pictograms-react';
import { ViewOff16, View16 } from '@carbon/icons-react';
import EnquiryInfoLegend from './enquiryInfoLegend';
import { SkeletonText, Button } from 'carbon-components-react';
import { toLocalDateTimeFormat, getDaysFromNow } from 'utilities/helper.util';
import i18n from 'i18n';
import { configureStore as store } from '../../../../redux/store';

const {
  modules,
  currentAOIDarkSymbol,
  currentAOIlightOutlineSymbol,
  currentAOIlightSymbol,
  previewAOIDarkSymbol,
  previewAOIlightSymbol
} = MapSettings;

export const AoiDetail: React.FC<Props> = ({
  orgProfile,
  currentActiveAoi,
  aoiLayerExtent,
  aoiLayerFeatures,
  errMsg,
  loadingState,
  updateState,
  organisationId,
  openPanel,
  historyReady,
  geoJson,
  updateMode,
  currentInfo,
  previewInfo,
  clippedWarning,
  previewId,
  previewIdFromFile,
  baseMapTheme
}) => {
  const [mapReady, setMapReady] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [hasNoAOI, setHasNoAOI] = useState<boolean>(false);
  const [activeTool, setActiveTool] = useState<string>('selector');
  const [isRemoveEnable, setIsRemoveEnable] = useState<boolean>(false);
  const [toolDisabled, setToolDisabled] = useState<boolean>(false);
  const [hideCurrentAoi, setHideCurrentAoi] = useState<boolean>(false);
  const [aoiCreatedAt, setAoiCreatedAt] = useState<string>('');
  const [aoiAge, setAoiAge] = useState<number | null>(null);
  const [aoiType, setAoiType] = useState<string | null>(null);
  const legendDiv = useRef<HTMLDivElement>();
  const layerViewHandler = useRef<any>();
  const previewLayerSymbol = useRef<any>();
  const orgIdRef = useRef<string>();
  const { loadedSketchViewModel, loadedMap } = window;
  const extraMapModules = [modules.webMercatorUtils, modules.featureLayer];
  const dispatch = useDispatch();
  const alertGroup = 'map-alert';

  const revertAoi = async () => {
    setHideCurrentAoi(false);
    setIsEditMode(false);
    clearGraphics();
    onClickTools('selector');
    const aoiLayer = window.loadedMap.findLayerById('current-aoi-layer');
    if (aoiLayer) {
      dispatch(queryFeatureLayerExtentStart(aoiLayer));
    }
    setPreviewLayerTransparent(true, previewLayerSymbol.current);
    dispatch(revertUploadNewAOI());
  };

  const applyAoi = async () => {
    if (previewId && organisationId) {
      const payload = {
        data: { previewId },
        organisationId
      };
      dispatch(putNewAOIStart(payload));
    }
  };

  const previewAoi = useCallback(
    (data) => {
      if (data && organisationId) {
        dispatch(
          getPreviewAOIInfoStart({
            data,
            organisationId
          })
        );
      }
    },
    [organisationId, dispatch]
  );

  const onMapReady = () => {
    const { loadedMapModules, loadedMap, loadedView } = window;
    const { Config, FeatureLayer } = loadedMapModules;
    Config.request.interceptors.push(
      {
        urls: getFeatureServerUrl('AreasOfInterest'),
        before: async (params: any) => {
          await featureRequestInterceptors(params);
          if (
            params.requestOptions.query &&
            !params.requestOptions.query.geometryPrecision
          ) {
            params.requestOptions.query.geometryPrecision = 6;
          }
        }
      },
      {
        urls: getFeatureServerUrl('AreasOfInterest_previews'),
        before: async (params: any) => {
          await featureRequestInterceptors(params);
          if (
            params.requestOptions.query &&
            !params.requestOptions.query.geometryPrecision
          ) {
            params.requestOptions.query.geometryPrecision = 6;
          }
        }
      }
    );

    const currentLayer = new FeatureLayer({
      id: 'current-aoi-layer',
      url: getFeatureServerUrl('AreasOfInterest'),
      outFields: ['objectid'],
      definitionExpression: 'deleted_at IS NULL',
      renderer: {
        type: 'simple',
        symbol:
          baseMapTheme === 'dark' ? currentAOIDarkSymbol : currentAOIlightSymbol
      }
    });

    const previewLayer = new FeatureLayer({
      id: 'preview-aoi-layer',
      url: getFeatureServerUrl('AreasOfInterest_previews'),
      outFields: ['objectid'],
      definitionExpression: 'group_id = 0',
      renderer: {
        type: 'simple',
        symbol:
          baseMapTheme === 'dark' ? previewAOIDarkSymbol : previewAOIlightSymbol
      }
    });

    // Only add layers if they don't already exist to prevent duplicates
    const existingCurrentLayer = loadedMap.findLayerById('current-aoi-layer');
    const existingPreviewLayer = loadedMap.findLayerById('preview-aoi-layer');

    if (!existingCurrentLayer) {
      loadedMap.add(currentLayer, 0);
    }
    if (!existingPreviewLayer) {
      loadedMap.add(previewLayer, 1);
    }

    if (!legendDiv.current) {
      const legendContainer = document.createElement('div');
      legendContainer.className = 'map--legend-widget';
      loadedView.ui.add(legendContainer, 'top-right');
      legendDiv.current = legendContainer;
    }

    setMapReady(true);
  };

  const endDrawing = () => {
    if (loadedSketchViewModel) {
      loadedSketchViewModel.cancel();
    }
  };

  const startDrawing = (tool: string) => {
    if (loadedSketchViewModel) {
      loadedSketchViewModel.create(tool, { mode: 'click' });
    }
  };

  const onClickTools = (tool: string) => {
    switch (tool) {
      case 'search': {
        endDrawing();
        activeTool !== 'search'
          ? setActiveTool('search')
          : setActiveTool('selector');
        break;
      }
      case 'selector': {
        endDrawing();
        setActiveTool('selector');
        break;
      }
      case 'polyline': {
        startDrawing('polyline');
        setActiveTool('polyline');
        break;
      }
      case 'polygon': {
        startDrawing('polygon');
        setActiveTool('polygon');
        break;
      }
      case 'remove': {
        if (loadedSketchViewModel) {
          loadedSketchViewModel.delete();
        }
        break;
      }
      case 'home': {
        if (
          aoiLayerExtent &&
          typeof aoiLayerExtent !== 'string' &&
          !aoiLayerExtent.noRecord
        ) {
          zoomToExtent(aoiLayerExtent);
        }
      }
    }
  };

  const resetTools = () => {
    setIsRemoveEnable(false);
    setActiveTool('selector');
  };

  const onEditDrawingStart = () => {
    setIsRemoveEnable(true);
  };

  const onCompleteDrawing = (param: MapDrawFuncParams) => {
    resetTools();
    try {
      const geoJson = getGeoJsonFromGraphics(param, previewLayerSymbol.current);
      if (!geoJson) {
        dispatch(setPreviewId({ previewId: undefined }));
        dispatch(
          addAlert(
            alertGroup,
            'warning',
            'You have no AOI shapes. Draw a new AOI to continue and save.'
          )
        );
      }
      dispatch(updateGeoJsonFromMap(geoJson));
    } catch (e) {
      let errorName = 'Error';
      let errorMessage = undefined;
      if (e instanceof Error) {
        errorName = e.name;
        errorMessage = e.message;
      }
      dispatch(addAlert(alertGroup, 'error', errorName, errorMessage));
    }
  };

  const refreshAoi = useCallback(() => {
    setHideCurrentAoi(false);
    if (loadedMap) {
      const aoiLayer = loadedMap.findLayerById('current-aoi-layer');
      if (aoiLayer) {
        aoiLayer.refresh();
        setAoiType(null);
        dispatch(initialAOIPageStart(organisationId));
        dispatch(queryFeatureLayerExtentStart(aoiLayer));
        dispatch(revertUploadNewAOI());
      }
    }
  }, [loadedMap, organisationId, dispatch]);

  const enterEditMode = () => {
    addAoiToDrawingLayer(
      aoiLayerFeatures || undefined,
      previewLayerSymbol.current
    );
    dispatch(removeAlertByGroup(alertGroup));
    setIsEditMode(true);
  };

  const onSelectGraphics = (results: any[]) => {
    const drawing = results.find(
      (r) => r?.graphic?.layer?.id === 'drawing-layer'
    );
    if (drawing) {
      setPreviewLayerTransparent(true, previewLayerSymbol.current);
      setDrawingLayerTransparent(false, previewLayerSymbol.current);
    }
  };

  useEffect(() => {
    if (geoJson) {
      if (mapReady && updateMode === 'boundary') {
        addGraphicFromGeoJson(geoJson, previewLayerSymbol.current);
        dispatch(removeAlertByGroup(alertGroup));
        setIsEditMode(true);
      }
      previewAoi(geoJson);
    } else {
      clearGraphics();
    }
  }, [geoJson, mapReady, updateMode, previewAoi, baseMapTheme, dispatch]);

  useEffect(() => {
    if (updateState === 'fail' && typeof errMsg === 'string' && errMsg) {
      const msg = i18n.exists(`AOI.shape.error.${errMsg}`)
        ? i18n.t(`AOI.shape.error.${errMsg}`)
        : errMsg;
      dispatch(addAlert(alertGroup, 'error', 'Error', msg));
    }
    updateState !== 'processing' && setIsEditMode(false);
    if (updateState === 'success') {
      refreshAoi();
      dispatch(
        addAlert(
          alertGroup,
          'success',
          'Successfully updated area of interest.'
        )
      );
    }
    enableClickOnMap(updateState !== 'processing');
    setToolDisabled(updateState === 'processing');
  }, [updateState, errMsg, dispatch, refreshAoi]);

  useEffect(() => {
    if (aoiLayerExtent) {
      if (typeof aoiLayerExtent !== 'string') {
        if (!aoiLayerExtent.noRecord) {
          zoomToExtent(aoiLayerExtent);
          window.mapOriginalExtent = aoiLayerExtent;
        }
      }
    }
  }, [aoiLayerExtent, dispatch]);

  useEffect(() => {
    if (currentActiveAoi) {
      if (currentActiveAoi === 'empty') {
        setHasNoAOI(true);
      } else {
        historyReady();
        setHasNoAOI(false);
        setAoiCreatedAt(toLocalDateTimeFormat(currentActiveAoi.createdAt));
        setAoiAge(getDaysFromNow(currentActiveAoi.createdAt));
        dispatch(
          getCurrentAOIInfoStart({
            aoiId: currentActiveAoi.id,
            organisationId: orgIdRef.current
          })
        );
      }
    }
  }, [currentActiveAoi, historyReady, dispatch]);

  useEffect(() => {
    if (orgProfile?.Tenant?.bbox && hasNoAOI) {
      const bbox =
        typeof orgProfile.Tenant.bbox === 'string'
          ? JSON.parse(orgProfile.Tenant.bbox)
          : orgProfile.Tenant.bbox;
      zoomToTenantExtent(bbox);
    }
  }, [orgProfile, hasNoAOI]);

  useEffect(() => {
    if (organisationId) {
      orgIdRef.current = organisationId;
    }
  }, [organisationId]);

  useEffect(() => {
    if (typeof currentInfo === 'string') {
      const msg = i18n.exists(`AOI.shape.error.${currentInfo}`)
        ? i18n.t<string>(`AOI.shape.error.${currentInfo}`)
        : currentInfo;
      dispatch(addAlert(alertGroup, 'error', 'Error', msg));
    } else {
      currentInfo && setAoiType(currentInfo.type);
    }
  }, [currentInfo, dispatch]);

  useEffect(() => {
    if (typeof previewInfo === 'string') {
      const msg = i18n.exists(`AOI.shape.error.${previewInfo}`)
        ? i18n.t<string>(`AOI.shape.error.${previewInfo}`)
        : previewInfo;
      dispatch(addAlert(alertGroup, 'error', 'Error', msg));
    }
  }, [previewInfo, dispatch]);

  useEffect(() => {
    if (clippedWarning) {
      const notificationProps = {
        title: clippedWarning,
        hideCloseButton: true,
        lowContrast: true
      };
      dispatch(
        addAlert('aoi-clipped-warning', 'warning', '', '', {
          withClose: false,
          type: 'inline',
          notificationProps
        })
      );
    } else {
      dispatch(removeAlertByGroup('aoi-clipped-warning'));
    }
  }, [clippedWarning, dispatch]);

  /**
   * Updates the renderer symbols for current and preview AOI layers.
   *
   * WHY: Layer renderers need to update when theme or edit mode changes
   * to provide visual feedback to the user.
   *
   * HOW: Creates a new renderer object which triggers ArcGIS to re-render
   * the layer. The renderer replacement should be atomic and not stack
   * multiple renders.
   */
  useEffect(() => {
    if (mapReady) {
      const currentLayer = window.loadedMap.findLayerById('current-aoi-layer');
      if (currentLayer) {
        currentLayer.renderer = {
          type: 'simple',
          symbol:
            baseMapTheme === 'dark'
              ? currentAOIDarkSymbol
              : isEditMode
              ? currentAOIlightSymbol
              : currentAOIlightOutlineSymbol
        };
      }
      const previewLayer = window.loadedMap.findLayerById('preview-aoi-layer');
      if (previewLayer) {
        previewLayer.renderer = {
          type: 'simple',
          symbol:
            baseMapTheme === 'dark'
              ? previewAOIDarkSymbol
              : previewAOIlightSymbol
        };
      }
      previewLayerSymbol.current =
        baseMapTheme === 'dark' ? previewAOIDarkSymbol : previewAOIlightSymbol;
    }
  }, [baseMapTheme, mapReady, isEditMode]);

  useEffect(() => {
    if (mapReady && aoiType !== null) {
      const layer = window.loadedMap.findLayerById('current-aoi-layer');
      if (layer && aoiType === 'DRAW') {
        dispatch(queryFeatureLayerFeaturesStart(layer));
      }
    }
  }, [aoiType, mapReady, dispatch]);

  useEffect(() => {
    if (mapReady) {
      const layer = window.loadedMap.findLayerById('preview-aoi-layer');
      if (layer) {
        if (previewId) {
          layer.definitionExpression = `group_id = ${previewId}`;
          const layerView = window.loadedView.layerViews.find(
            (view: any) => view?.layer?.id === 'preview-aoi-layer'
          );
          if (layerView && !layerViewHandler.current) {
            layerViewHandler.current = layerView.watch(
              'updating',
              (value: any) => {
                if (!value) {
                  setTimeout(() => {
                    setDrawingLayerTransparent(
                      true,
                      previewLayerSymbol.current
                    );
                  }, 100);
                  setPreviewLayerTransparent(false, previewLayerSymbol.current);
                  layerViewHandler.current && layerViewHandler.current.remove();
                  layerViewHandler.current = undefined;
                }
              }
            );
          }
        } else {
          layer.definitionExpression = 'group_id = 0';
        }
      }
    }
  }, [previewId, mapReady, updateMode, previewAoi, dispatch]);

  useEffect(() => {
    if (previewIdFromFile) {
      setIsEditMode(true);
      previewAoi({ previewId: previewIdFromFile });
      const layer = window.loadedMap.findLayerById('preview-aoi-layer');
      if (layer) {
        layer.definitionExpression = `group_id = ${previewIdFromFile}`;
        dispatch(queryFeatureLayerExtentStart(layer));
      }
    }
  }, [previewIdFromFile, previewAoi, dispatch]);

  useEffect(() => {
    if (mapReady) {
      const layer = window.loadedMap.findLayerById('current-aoi-layer');
      if (layer) {
        layer.visible = !hideCurrentAoi;
      }
    }
  }, [hideCurrentAoi, mapReady]);

  useEffect(() => {
    if (mapReady) {
      const layer = window.loadedMap.findLayerById('current-aoi-layer');
      if (layer && organisationId) {
        layer.definitionExpression = `deleted_at IS NULL AND organisation_id=${organisationId}`;
        dispatch(queryFeatureLayerExtentStart(layer));
      }
    }
  }, [organisationId, mapReady, dispatch]);

  useComponentMount(() => {
    return () => {
      dispatch(removeAlertByGroup(alertGroup));
      dispatch(clearFeatureLayerLayerExtent());
      dispatch(revertUploadNewAOI());
    };
  });

  return (
    <div className="aoi-details-container">
      {(!hasNoAOI || isEditMode) && <hr className="section--divider" />}
      <div
        className={`aoi-details-info-container${
          hasNoAOI && !isEditMode ? '--empty' : ''
        }`}
      >
        <div
          className={`aoi-details-info${
            hasNoAOI && !isEditMode ? '--empty' : ''
          }`}
        >
          {hasNoAOI && !isEditMode && (
            <>
              <div className="aoi-no-record-image">
                <Networking06 width={96} height={96} />
              </div>
              <div className="aoi-info-text">There is no AOI defined yet</div>
            </>
          )}
          {!hasNoAOI && (
            <>
              {aoiCreatedAt && typeof aoiAge === 'number' ? (
                <div className="aoi-info-text">
                  <strong>Usage period</strong> {aoiCreatedAt} → Current (age{' '}
                  {aoiAge > 0 ? aoiAge : 'less than 1 '}{' '}
                  {aoiAge > 1 ? 'days' : 'day'})
                </div>
              ) : (
                <SkeletonText />
              )}
              {currentInfo?.area ? (
                <div className="aoi-info-text">
                  <strong>Area</strong> {currentInfo.area} km²{' '}
                  {previewInfo?.area && isEditMode && (
                    <span className="aoi-preview-info">
                      (New area {previewInfo.area} km²)
                    </span>
                  )}
                </div>
              ) : currentInfo == null ? (
                <SkeletonText />
              ) : null}
            </>
          )}
        </div>
        {['success', 'fail'].includes(loadingState) && (
          <Controls
            isEmpty={hasNoAOI && !isEditMode}
            isEditMode={isEditMode}
            updateState={updateState}
            aoiType={aoiType}
            disabledSave={!previewId}
            onClickEdit={() => enterEditMode()}
            onClickUpload={() => openPanel('upload')}
            onClickBoundary={() => openPanel('boundary')}
            onClickRevert={() => revertAoi()}
            onClickSave={() => applyAoi()}
          />
        )}
      </div>
      <div
        className={`aoi-details-map--view${
          hasNoAOI && !isEditMode ? '--empty' : ''
        } ${clippedWarning ? 'hasWarning' : ''}`}
      >
        <Map
          onSelect={onSelectGraphics}
          showHomeButton={true}
          showbaseMapButton={!hasNoAOI || isEditMode}
          showSearchButton={!hasNoAOI || isEditMode}
          showCurrentLocationButton={false}
          onCompleteDrawing={onCompleteDrawing}
          onCancelDrawing={() => resetTools()}
          onEditDrawingStart={onEditDrawingStart}
          onReady={onMapReady}
          extraModules={extraMapModules}
        />
        {legendDiv.current &&
          ReactDom.render(
            <Provider store={store}>
              <div className="aoi-details-legend-container">
                {isEditMode && !previewIdFromFile && (
                  <div className="legend-box tools">
                    <Tools
                      allButtonDisabled={toolDisabled}
                      activeTool={activeTool}
                      removeButtonDisabled={!isRemoveEnable}
                      onClickTools={onClickTools}
                    />
                  </div>
                )}

                <EnquiryInfoLegend
                  previewAoi={() => previewAoi({ previewId })}
                  currentInfo={currentInfo}
                  previewInfo={previewInfo}
                  isEditMode={isEditMode}
                />
                {isEditMode && (
                  <div className="legend-box polygon">
                    <div className="legend--current">
                      <div className="legend--label">
                        <span>
                          <div
                            className={`line${
                              baseMapTheme === 'dark' ? ' dark' : ''
                            }`}
                          />
                        </span>
                        <span>Current AOI</span>
                      </div>
                      <Button
                        className="hide-show-btn"
                        renderIcon={hideCurrentAoi ? View16 : ViewOff16}
                        iconDescription={hideCurrentAoi ? 'Show' : 'Hide'}
                        tooltipPosition="top"
                        hasIconOnly
                        kind="ghost"
                        onClick={() => setHideCurrentAoi(!hideCurrentAoi)}
                      />
                    </div>
                    <div className="legend--new">
                      <span>
                        <div
                          className={`line${
                            baseMapTheme === 'dark' ? ' dark' : ''
                          }`}
                        />
                      </span>
                      <span>New AOI</span>
                    </div>
                  </div>
                )}
              </div>
            </Provider>,
            legendDiv.current
          )}
      </div>
    </div>
  );
};

type ownProps = {
  organisationId: string;
  openPanel: (panel: string) => void;
  historyReady: () => void;
};

const mapStateToProps = ({
  aoiCache,
  mapCache,
  memberCache,
  app
}: RootState) => ({
  orgProfile: memberCache.orgProfile,
  aoiLayerExtent: mapCache.featureLayerExtent,
  aoiLayerFeatures: mapCache.featureLayerfeatures,
  currentActiveAoi: aoiCache.currentActiveAoi,
  geoJson: aoiCache.geoJson,
  updateMode: aoiCache.aoiUpdateMode,
  errMsg: aoiCache.errMsg,
  loadingState: aoiCache.loadingState,
  updateState: aoiCache.updateState,
  currentInfo: aoiCache.currentInfo,
  previewInfo: aoiCache.previewInfo,
  clippedWarning: aoiCache.clippedWarning,
  previewId: aoiCache.previewId,
  previewIdFromFile: aoiCache.previewIdFromFile,
  baseMapTheme: app.baseMapTheme
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector> & ownProps;

AoiDetail.displayName = 'AreaOfInterestDetail';

export default React.memo(connector(AoiDetail));
